import React from 'react';
import Header from '../Header';
import Footer from '../Footer';
import useRouteMeta from '../../../hooks/useRouteMeta';
import './PrivacyPage.css';
import { Link, useNavigate } from 'react-router-dom';
import { IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { merchantAuthAPI } from '../../../services/merchantAPI';

const PrivacyPage = () => {
  // SEO Meta Tags - Automatically applied from config
  useRouteMeta();
  const navigate = useNavigate();
  const [showWhatsAppSmsAuth, setShowWhatsAppSmsAuth] = React.useState(true);

  React.useEffect(() => {
    const checkAuthVisibility = async () => {
      try {
        const response = await merchantAuthAPI.checkAuthVisibility();
        if (response.data && typeof response.data.show_whatsapp_sms_auth === 'boolean') {
          setShowWhatsAppSmsAuth(response.data.show_whatsapp_sms_auth);
        }
      } catch (error) {
        console.error('❌ Failed to check auth visibility:', error);
      }
    };
    checkAuthVisibility();
  }, []);

  return (
    <div className="privacy-page">
      <Header />

      <div className="container">
        <div className="row">
          <div className="col-md-12 privacy-page-content page-header-container">
            <div className="back-btn-container">
              <IconButton onClick={() => navigate(-1)} sx={{ fontSize: { xs: '15px', md: '17px' }, padding: { xs: '5px 15px', md: '7px 15px' } }} color="inherit" style={{ border: '1px solid #000000', color: '#000000', borderRadius: '33px', fontFamily: 'var(--font-montserrat-medium)', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '2px' }}>
                <ArrowBackIcon fontSize='1.2rem' /> Back
              </IconButton>
            </div>
            <h1>Privacy Policy</h1>
          </div>
        </div>
        <div className="row justify-content-center">
          <div className="col-md-12 privacy-page-content-2">
            <div className='row'>
              <div className='col-md-12 legal-text'>
                <p><span className='legal-bold'>Effective Date:</span> 15th August 2025</p>
                <p>Glammify Private Limited (“we,” “our,” “us”) respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, store, share, and safeguard your data when you use our website, mobile application, and related services (collectively, the “Services”).</p>
                <p>By accessing or using our Services, you agree to the terms of this Privacy Policy. If you do not agree, please do not use our Services.</p>
              </div>
              <div className='col-md-12 legal-text'>
                <h3>1. Information We Collect</h3>
                <p>We may collect the following types of information:</p>
                <h6>a. Information You Provide to Us</h6>
                <ul>
                  <li>Name, email address, phone number, business details, billing/shipping address</li>
                  <li>Account login credentials</li>
                  <li>Payment details (processed through secure third-party payment gateways)</li>
                  <li>Product images, videos, and other content you upload for processing through our platform</li>
                  <li>Feedback, inquiries, and other communications</li>
                  <li>Your social media credentials for direct posting the creatives</li>
                </ul>
              </div>
              <div className='col-md-12 legal-text'>
                <h6>b. Information Collected Automatically</h6>
                <p>When you use our Services, we may automatically collect:</p>
                <ul>
                  <li>Device information (type, model, operating system, browser)</li>
                  <li>IP address and geolocation data</li>
                  <li>Usage data (pages visited, time spent, actions performed)</li>
                  <li>Cookies and similar tracking technologies (see Section 8)</li>
                </ul>
              </div>
              <div className='col-md-12 legal-text'>
                <h6>c. Information from Third Parties</h6>
                <p>We may receive information from:</p>
                <ul>
                  <li>Social media accounts you connect</li>
                  <li>Integrated e-commerce platforms (e.g., Shopify, WooCommerce, Magento)</li>
                  <li>Marketing and advertising partners</li>
                </ul>
              </div>
              <div className='col-md-12 legal-text'>
                <h3>2. How We Use Your Information</h3>
                <p>We use your information for:</p>
                <ul>
                  <li>Providing, maintaining, and improving our Services</li>
                  <li>Processing your orders and transactions</li>
                  <li>Customizing and enhancing product images/videos per your request</li>
                  <li>Communicating with you (customer support, updates, offers)</li>
                  <li>Sending marketing communications (with your consent)</li>
                  <li>Analyzing usage patterns to improve user experience</li>
                  <li>Ensuring compliance with laws and preventing fraud or misuse</li>
                </ul>
              </div>
              <div className='col-md-12 legal-text'>
                <h3>3. How We Share Your Information</h3>
                <p>We do not sell your personal information.<br />
                  We may share it only in these circumstances:</p>
                <ul>
                  <li><span className='legal-bold'>Service Providers:</span> Trusted partners who help operate our Services (hosting, payment processing, analytics, AI model providers)</li>
                  <li><span className='legal-bold'>Business Transfers:</span> In case of merger, acquisition, or sale of assets</li>
                  <li><span className='legal-bold'>Legal Requirements:</span> When required by law, regulation, or legal process</li>
                  <li><span className='legal-bold'>Consent:</span> With your explicit consent</li>
                </ul>
              </div>
              <div className='col-md-12 legal-text'>
                <h3>4. Data Retention</h3>
                <p>We retain your information only as long as necessary for:</p>
                <ul>
                  <li>Providing Services</li>
                  <li>Fulfilling legal and tax obligations</li>
                  <li>Resolving disputes and enforcing agreements</li>
                </ul>
                <p>If you close your account, we may retain limited data for legal compliance and security purposes.</p>
              </div>
              <div className='col-md-12 legal-text'>
                <h3>5. Data Security</h3>
                <p>We implement appropriate technical and organizational measures to protect your data, including:</p>
                <ul>
                  <li>Encrypted data transmission (HTTPS/SSL)</li>
                  <li>Secure cloud storage</li>
                  <li>Restricted access to personal data</li>
                  <li>Regular security audits</li>
                </ul>
                <p>However, no method of transmission or storage is 100% secure, and we cannot guarantee absolute security.</p>
              </div>
              <div className='col-md-12 legal-text'>
                <h3>6. Your Rights</h3>
                <p>Depending on your location, you may have rights to:</p>
                <ul>
                  <li>Access, update, or delete your personal data</li>
                  <li>Request a copy of your data</li>
                  <li>Opt-out of marketing communications</li>
                  <li>Restrict or object to processing of your data</li>
                  <li>Withdraw consent at any time</li>
                </ul>
                <p>To exercise these rights, contact us at <a href='mailto:support@glammify.ai'>support@glammify.ai</a></p>
              </div>
              <div className='col-md-12 legal-text'>
                <h3>7. International Data Transfers</h3>
                <p>If you access our Services from outside India, your data may be transferred to and processed in countries with different data protection laws. We take steps to ensure your information remains protected.</p>
              </div>
              <div className='col-md-12 legal-text'>
                <h3>8. Cookies & Tracking Technologies</h3>
                <p>We use cookies, pixels, and similar technologies to:</p>
                <ul>
                  <li>Remember your preferences</li>
                  <li>Analyze site performance</li>
                  <li>Deliver targeted advertisements</li>
                </ul>
                <p>You can manage or disable cookies in your browser settings, but some features may not work properly.</p>
              </div>


              <div className='col-md-12 legal-text'>
                <h3>9. Third-Party Links</h3>
                <p>Our Services may contain links to third-party websites or tools. We are not responsible for the privacy practices or content of these sites.</p>
              </div>


              <div className='col-md-12 legal-text'>
                <h3>10. Children’s Privacy</h3>
                <p>Our Services are not intended for children under 18 (or the minimum age in your jurisdiction). We do not knowingly collect personal information from children.</p>
              </div>


              <div className='col-md-12 legal-text'>
                <h3>11. Changes to This Policy</h3>
                <p>We may update this Privacy Policy from time to time. We will notify you of changes by posting the new version on our website with the updated “Effective Date.”</p>
              </div>

              <div className='col-md-12 legal-text'>
                <h3>12. Contact Us</h3>
                <p> If you have any questions about this Privacy Policy, please contact us at:</p>
                <ul>
                  <li>Email: <Link to='mailto:support@glammify.ai'>support@glammify.ai</Link></li>
                  {showWhatsAppSmsAuth && (
                    <li>Phone: <Link to='tel:+91 7039796526'>+91 7039796526</Link></li>
                  )}
                  <li>Address: Crystal Plaza, 604/605, Guru Hargovindji Rd, next to Atul Projects, Chakala, Andheri East, Mumbai, Maharashtra 400099</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>


      {showWhatsAppSmsAuth && <Footer />}
    </div>
  );
};

export default PrivacyPage;
